import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { FiltersDrawerSortItems } from './FiltersDrawerSortItems';
import {
  replaceUrlPath,
  separateBooleanProperties,
  updateURLParameters,
} from './FiltersDrawer.helpers';
import { BooleanOptions } from './BooleanOptions';
import * as S from './FiltersDrawer.styles';
import {
  BooleanCollection,
  CatalogueDrawerProps,
  CategoryData,
  Collection,
  RangeCollection,
} from './FiltersDrawer.types';
import { RangeOptions } from './RangeOptions';
import { CollectionSelect } from './CollectionSelect';
import {
  PRICE_INPUT_MAX_LENGTH,
  PRICE_INPUT_PRECISION,
} from './FiltersDrawer.constants';
import { countActiveFilters, getSellerTypeOptions } from './helpers';
import { BooleanChips } from './BooleanChips';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { Drawer } from '@/components/Drawer/Drawer';
import { Select } from '@/components/UI/Select/Select';
import { Input } from '@/components/UI/Input/Input';
import {
  CatalogueDrawer,
  CatalogueDrawerRef,
} from '@/components/CatalogueDrawer/CatalogueDrawer';
import { CitiesDrawer } from '@/components/CitiesDrawer/CitiesDrawer';
import {
  Category,
  PropertyOption,
  PropertyType,
} from '@/api/types/categories.types';
import { GetRegionsResponse, Region } from '@/api/types/regions.types';
import { AdOrder } from '@/api/types/ads.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { formatNumber } from '@/utils/helpers';
import {
  mapBooleanPropertyOptionsToBooleanCollection,
  mapCollectionPropertyOptionsToCollection,
  mapRangePropertyOptionsToRangeCollection,
} from '@/hooks/useParseQuery/useParseQuery.helpers';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useCategoryProperties } from '@/hooks/useCategoryProperties';
import { QUERY } from '@/constants/common';

export const FiltersDrawer = ({
  isOpen,
  close,
  setFilterCount,
}: CatalogueDrawerProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const [prices, setPrices] = useState<{
    from: string | null;
    to: string | null;
  }>({ from: null, to: null });
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isCitiesOpen, setIsCitiesOpen] = useState(false);
  const [sort, setSort] = useState<AdOrder>(AdOrder.DateDesc);
  const [city, setCity] = useState<Region | null>(null);
  const [sellerType, setSellerType] = useState<boolean | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryData | null>(null);
  const [booleanCollection, setBooleanCollection] = useState<BooleanCollection>(
    {}
  );
  const [rangeCollection, setRangeCollection] = useState<RangeCollection>({});
  const [collection, setCollection] = useState<Collection>({});
  const catalogueDrawerRef = useRef<CatalogueDrawerRef>(null);
  const {
    propertyOptions: {
      boolean: booleanPropertyOptions,
      range: rangePropertyOptions,
      collection: collectionPropertyOptions,
    },
    price: { from: priceFrom, to: priceTo },
    regionLegacyId,
    order,
    isShop,
  } = useParseQuery();
  const { clearUrl } = useUrlAndHashFromPath();
  const { currentCategory, isAllCategories } = useCurrentCategory();
  const { categoryProperties, refetchCategoryProperties } =
    useCategoryProperties();

  const { data: regions } = useQuery<GetRegionsResponse>(GET_REGIONS);

  const isFirstLevel = router.query.slug?.length === 1;
  const isSubcategories = router.query.slug?.length === 2;
  const { booleanProperties, restProperties } =
    separateBooleanProperties(categoryProperties);
  const sellerTypeOptions = getSellerTypeOptions(t).map(
    ({ label, queryValue }) => ({
      dataTestId: 'seller-type-option',
      label,
      value: queryValue === undefined ? undefined : queryValue === 'true',
    })
  );

  const filtersCount = useMemo(() => {
    const result = countActiveFilters({
      baseConditions: [
        categoryData,
        prices.from || prices.to,
        city,
        sort && sort !== AdOrder.DateDesc,
        sellerType !== null,
      ],
      collectionValues: Object.values(collection),
      booleanValues: Object.values(booleanCollection),
      rangeValues: Object.values(rangeCollection),
      hasRangeValue: value => Boolean(value?.[0] || value?.[1]),
    });

    setFilterCount(result);
    return result;
  }, [
    categoryData,
    prices,
    city,
    sort,
    sellerType,
    collection,
    booleanCollection,
    rangeCollection,
    setFilterCount,
  ]);

  const isResetButtonDisabled =
    (filtersCount === 1 && !!categoryData) || !filtersCount;

  function clearAllCollections() {
    setCollection({});
    setBooleanCollection({});
    setRangeCollection({});
  }

  function resetForm(shouldResetCategory = false) {
    setPrices({ from: null, to: null });
    setCity(null);
    setSort(AdOrder.DateDesc);
    setSellerType(null);
    clearAllCollections();

    if (shouldResetCategory) {
      setCategoryData(null);
      catalogueDrawerRef.current?.reset();
    }
  }

  function toggleCategoriesSubDrawer() {
    setIsCategoriesOpen(prev => !prev);
  }

  function toggleCitiesSubDrawer() {
    setIsCitiesOpen(prev => !prev);
  }

  function onSelectCategory(category: Category, subcategory?: Category | null) {
    setCategoryData({
      category,
      subcategory,
      customPath: !subcategory ? category.path : undefined,
    });

    clearAllCollections();
    refetchCategoryProperties({ path: subcategory?.path || category?.path });
  }

  function onPropertySelect(
    option: PropertyOption,
    category: Category,
    path: string
  ) {
    const cleanPath = path.split('?')[0].substring(1);

    clearAllCollections();
    setCategoryData({ category, customPath: cleanPath });
    setCollection({ [option.propertyLegacyId]: option.legacyResourceId });
    refetchCategoryProperties({ path: cleanPath });
  }

  function applyFilters() {
    const path = categoryData?.subcategory?.path || categoryData?.customPath;
    const newParams = {
      categoryId: categoryData?.subcategory?.id ?? '',
      [`${QUERY.q}[${QUERY.price}][]`]: [prices.from ?? '', prices.to ?? ''],
      [`${QUERY.q}[${QUERY.isShop}]`]:
        sellerType === null || sellerType === undefined
          ? ''
          : String(sellerType),
      [`${QUERY.q}[${QUERY.regionId}]`]: city?.legacyResourceId ?? '',
      order: sort.toLowerCase(),
      [QUERY.keywordsSource]: KeywordsSource.Typewritten,
    };

    const newURL = updateURLParameters({
      url: window.location.href,
      params: newParams,
      booleanCollection,
      rangeCollection,
      collection,
      hasCategory: !!categoryData,
    });

    close();
    setTimeout(() => {
      if (!path || clearUrl === path) {
        router.push(newURL);
      } else if (path) {
        router.push(replaceUrlPath(newURL, path));
      }
    }, 0);
  }

  function changePrice(e: string, fieldName: keyof typeof prices) {
    setPrices({ ...prices, [fieldName]: e });
  }

  useEffect(() => {
    if (!currentCategory) return;

    if (isAllCategories) {
      setCategoryData(null);
    }

    if (isFirstLevel) {
      setCategoryData({ category: currentCategory, subcategory: undefined });
    }

    if (isSubcategories && currentCategory.parent) {
      setCategoryData({
        category: currentCategory.parent,
        subcategory: currentCategory,
      });
    }
    catalogueDrawerRef.current?.reset();
  }, [currentCategory, isOpen]);

  useEffect(() => {
    const booleanCollectionFromQuery =
      mapBooleanPropertyOptionsToBooleanCollection(booleanPropertyOptions);
    const rangeCollectionFromQuery =
      mapRangePropertyOptionsToRangeCollection(rangePropertyOptions);
    const collectionFromQuery = mapCollectionPropertyOptionsToCollection(
      collectionPropertyOptions
    );

    setBooleanCollection(booleanCollectionFromQuery);
    setRangeCollection(rangeCollectionFromQuery);
    setCollection(collectionFromQuery);
    setPrices({
      from: priceFrom
        ? formatNumber(priceFrom, PRICE_INPUT_PRECISION, true)
        : null,
      to: priceTo ? formatNumber(priceTo, PRICE_INPUT_PRECISION, true) : null,
    });
    setSort(order ?? AdOrder.DateDesc);
    setSellerType(isShop ?? null);
  }, [
    booleanPropertyOptions,
    rangePropertyOptions,
    collectionPropertyOptions,
    priceFrom,
    priceTo,
    order,
    isShop,
    isOpen,
  ]);

  useEffect(() => {
    if (regions) {
      setCity(
        regions.regions.find(
          r => Number(r.legacyResourceId) === Number(regionLegacyId)
        ) ?? null
      );
    }
  }, [regions, regionLegacyId, isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      close={close}
      title={t('common.filters')}
      right={{
        onClick: () => resetForm(false),
        isDisabled: isResetButtonDisabled,
      }}
      isInPortal
      dataTestId="filters-drawer"
      hasBottomMargin={false}
    >
      <S.DrawerItem data-testid="category-select">
        <Select
          isOpen={isCategoriesOpen}
          label={t('common.category')}
          toggle={toggleCategoriesSubDrawer}
          value={
            categoryData?.subcategory?.name ||
            categoryData?.category?.name ||
            ''
          }
          iconSrc={
            categoryData?.subcategory?.icon
              ? categoryData.subcategory.icon
              : categoryData?.category.icon
          }
          hasIcon
          appearance="filled"
          onClear={() => resetForm(true)}
        >
          <CatalogueDrawer
            title={t('catalogue.category')}
            isOpen={isCategoriesOpen}
            close={toggleCategoriesSubDrawer}
            onSelect={onSelectCategory}
            onPropertySelect={onPropertySelect}
            hasShopsLink={false}
            hasSubcategories
            isInPortal={false}
            ref={catalogueDrawerRef}
            searchPlaceholder={t('catalogue.category_search')}
          />
        </Select>
      </S.DrawerItem>

      <S.DrawerItem>
        <S.FieldWithTitleWrapper>
          <S.PriceFieldTitle>{t('common.price_azn')}</S.PriceFieldTitle>
          <S.TwoFieldsInRow>
            <Input
              value={prices.from?.toString() ?? ''}
              onChange={value => changePrice(value as string, 'from')}
              clear={() => setPrices({ ...prices, from: null })}
              type="search"
              appearance="filled"
              inputMode="decimal"
              placeholder={t('common.from')}
              label={t('common.from')}
              labelPosition="left"
              precision={PRICE_INPUT_PRECISION}
              customPaddingTop={2}
              className="string required form-control min"
              data-testid="price-from"
              maxLength={PRICE_INPUT_MAX_LENGTH}
            />
            <Input
              value={prices.to?.toString() ?? ''}
              onChange={value => changePrice(value as string, 'to')}
              clear={() => setPrices({ ...prices, to: null })}
              type="search"
              appearance="filled"
              inputMode="decimal"
              placeholder={t('common.to')}
              label={t('common.to')}
              labelPosition="left"
              precision={PRICE_INPUT_PRECISION}
              customPaddingTop={2}
              className="string required form-control max"
              data-testid="price-to"
              maxLength={PRICE_INPUT_MAX_LENGTH}
            />
          </S.TwoFieldsInRow>
        </S.FieldWithTitleWrapper>

        <BooleanChips
          title={t('common.seller_type')}
          value={sellerType ?? undefined}
          onChange={(value: boolean | undefined) =>
            setSellerType(value ?? null)
          }
          options={sellerTypeOptions}
        />

        <Select
          isOpen={isCitiesOpen}
          label={t('common.city')}
          toggle={toggleCitiesSubDrawer}
          value={city?.name ?? ''}
          onClear={() => setCity(null)}
          appearance="filled"
          dataTestId="city-select"
        >
          <CitiesDrawer
            isOpen={isCitiesOpen}
            close={toggleCitiesSubDrawer}
            selected={city?.name ?? ''}
            onSelect={setCity}
          />
        </Select>
      </S.DrawerItem>

      {!!restProperties.length && categoryData && (
        <S.DrawerItem>
          {restProperties.map(p => {
            switch (p.type) {
              case PropertyType.CollectionProperty:
                return (
                  <CollectionSelect
                    key={p.legacyResourceId}
                    propertyLegacyId={p.legacyResourceId}
                    options={p.options}
                    popularOptions={p.popularOptions}
                    collection={collection}
                    setCollection={setCollection}
                    allProperties={categoryProperties}
                    isFiltersOpen={isOpen}
                  />
                );

              case PropertyType.RangeProperty:
                return (
                  <RangeOptions
                    key={p.legacyResourceId}
                    property={p}
                    collection={rangeCollection}
                    setCollection={setRangeCollection}
                  />
                );

              default:
                return null;
            }
          })}
        </S.DrawerItem>
      )}

      {categoryData &&
        booleanProperties.map(bp => {
          return (
            <S.DrawerItem key={bp.legacyResourceId}>
              <BooleanOptions
                property={bp}
                collection={booleanCollection}
                setCollection={setBooleanCollection}
              />
            </S.DrawerItem>
          );
        })}

      <S.DrawerItem>
        <FiltersDrawerSortItems sort={sort} setSort={setSort} />
      </S.DrawerItem>

      <S.FiltersDrawerCTA>
        <S.ApplyButton
          data-testid="apply-filters"
          appearance="primary"
          isFullWidth
          onClick={applyFilters}
        >
          {t('common.show')}
        </S.ApplyButton>
      </S.FiltersDrawerCTA>
    </Drawer>
  );
};
